import { create } from "zustand";
import { Option, Some } from "@sniptt/monads";
import { differenceInMinutes, addMinutes } from "date-fns";
import jwtDecode from "jwt-decode";
import axios from "axios";
import * as Idm from "./data/Idm";
import * as Intent from "./data/Intent";
import { callOnAllTabs, sharedStorage } from "@harbor/plugin-utils";
import { fetchResourceDomain, ResourceDomain } from "./ResourceDomain";

const TOKEN_TTL_MINUTES = 60;

const enum AuthState {
   LoggedIn = "loggedIn",
   LoggedOut = "loggedOut",
   TenantSelection = "tenantSelection",
}

interface Session {
   email: string;
   userName: string;
   firstName?: string;
   lastName?: string;
   tenantId: string;
   intents: Intent.T[];
   activeResourceDomain?: ResourceDomain;
   expires: Date;
   timeoutWarning?: Date;
   csrf: string;
}

interface SessionStore {
   idleTimeoutMinutes: number;
   session: Session | null;

   /**
    * During a new token fetch, set this to the fetch promise.
    * If refreshToken gets called quickly multiple times, we can skip subsequent ones while one is already pending
    */
   refreshing: Promise<unknown> | null;

   /**
    * Refreshes the active session's
    *
    * @todo Check if this needs to be separate from session fetching. Maybe it can be all one thing
    */
   fetchIntents(): Promise<void>;

   /**
    * Initiate a session in the store
    */
   initializeSession(): Promise<void>;

   fetchIdlePrefs(): Promise<void>;

   /**
    * Ensure that this session's token is up to date.
    *
    * Tokens expire after a set amount of time and if they are not refreshed,
    * the backend APIs will start throwing Unauthorized errors. So we should
    * be refreshing them on a periodic basis
    */
   refreshSession(): Promise<void>;
   updateCsrf(csrf: string): void;
   updateTokenExpiryDate(expires: string): void;

   /**
    * Ends the user's session and returns them to the login screen
    */
   endSession(): void;

   /**
    *
    * @param date
    */
   setTimeoutWarning(date: string | null): void;
}

export type T = Session;

/**
 * Get a list of permitted operations for a specific resource
 */
export function permittedOperations(
   session: Session,
   resourceName: string
): string[] {
   const intent = session.intents.find(
      (it) => it.resourceName === resourceName
   );
   return intent?.operationNameSet || [];
}

/**
 * Create a map of intents by resource name to optimize lookup
 */
export function intentsNormalized(
   intents: Intent.T[]
): Record<string, Intent.T> {
   const normal = {} as Record<string, Intent.T>;
   intents.forEach((it) => {
      normal[it.resourceName] = it;
   });
   return normal;
}

async function getLogoutUrl(): Promise<string> {
   const { SessionStatus } = Idm;

   switch (await Idm.fetchSessionStatus()) {
      case SessionStatus.Active: {
         const params = new URLSearchParams({ logout: "type:normal" });
         return `${window.platformUI.auth.logoutPath}?${params.toString()}`;
      }

      default:
         return "/auth/login";
   }
}

const fetchTimeoutDate = () =>
   sharedStorage
      .fetchItem("cisco.dna.core.auth/expire")
      .then((x) => (x ? new Date(x) : undefined));

type TenantPreferenceResponse = {
   preferences: {
      sessionTimeOutMinutes: number | string;
      checkFailedLogin: boolean;
   };
};

export const useStore = create<SessionStore>((set, get) => ({
   session: null,
   refreshing: null,
   idleTimeoutMinutes: 15,

   fetchIntents: async () => {
      if (window.platformUI.auth.state !== AuthState.LoggedIn) return;
      if (window.platformUI.authSkip) return;
      if (!get().session) return;

      const intents = await Intent.fetchForCurrentUser();

      const session = get().session;
      if (!session) return;

      set({ session: { ...session, intents } });
   },

   fetchIdlePrefs: async () => {
      const { session } = get();
      if (!session) return;

      try {
         const res = await axios
            .get(`/idm/api/v1/im/tenants/${session.tenantId}/tenant-preference`)
            .then((r) => r.data as TenantPreferenceResponse);

         // The idle preference on the settings page is disabled
         // if account lockout is disabled,
         // so only use the timeout pref if the user has updated it
         if (res.preferences.checkFailedLogin) {
            const stm = res.preferences.sessionTimeOutMinutes;
            const minutes = typeof stm === "number" ? stm : parseInt(stm);
            set({ idleTimeoutMinutes: minutes });
         }
      } catch (err) {
         console.error("Failed to fetch tenant preferences", err);
      }
   },

   initializeSession: async () => {
      // todo: can we determine this by a missing JWT token?
      if (window.platformUI.auth.state !== AuthState.LoggedIn) {
         // localStorage.clear();
         return set({ session: null });
      }

      if (!window.platformUI.auth.token) {
         return set({ session: null });
      }

      const data = jwtDecode(window.platformUI.auth.token);
      const activeResourceDomain = data.activeRD
         ? await fetchResourceDomain(data.activeRD)
         : undefined;

      const syncExp = await sharedStorage
         .fetchItem("cisco.dna.core.auth/token-expire")
         .then((ex) => (ex ? new Date(ex).getTime() : null));

      const expires = Math.max(syncExp ?? 0, data.exp * 1000);

      // In case the user logs in with a new token but session storage wasn't cleared
      if (expires !== syncExp) {
         sharedStorage.removeItem("cisco.dna.core.auth/token-expire");
      }

      const csrf = window.platformUI.csrfToken;
      // in some automated login flows (i.e. cross launch) a new csrf token is generated when a tab is opened
      // this fn makes sure that all other tabs update their csrf in case they've been invalidated
      if (csrf) {
         get().updateCsrf(csrf);
      }

      set({
         refreshing: null,
         session: {
            email: data.email,
            userName: data.username,
            expires: new Date(expires),
            firstName: data.firstName,
            lastName: data.lastName,
            intents: [],
            tenantId: data.tenantId,
            activeResourceDomain,
            // Note: on load, the csrf token should be provided from the backend.
            // The fetch to idm is a fallback in case the cookie isn't set, which could be the case in some older versions.
            csrf:
               csrf ?? (await Idm.refreshToken().then((it) => it.csrf_token)),
            timeoutWarning: await fetchTimeoutDate(),
         },
      });

      await get().fetchIntents();
   },

   refreshSession: async () => {
      const { session, refreshing } = get();
      if (!session) return;

      // Prevent refreshing several times concurrently
      if (refreshing) return void (await refreshing);

      const timeLeftMinutes = differenceInMinutes(session.expires, new Date());
      if (timeLeftMinutes > 5) return;

      try {
         const refreshToken = Idm.refreshToken();
         set({ refreshing: refreshToken });

         const { csrf_token } = await refreshToken;
         get().updateCsrf(csrf_token);

         const expires = addMinutes(new Date(), TOKEN_TTL_MINUTES);
         get().updateTokenExpiryDate(expires.toISOString());
      } catch (err) {
         console.error("Unable to keep session refreshed", err);
         await get().endSession();
      }
   },

   updateCsrf: callOnAllTabs(
      "cisco.dna.core.auth/updateCsrf",
      (csrf: string) => {
         const { session } = get();
         if (!session) return;

         set({
            refreshing: null,
            session: { ...session, csrf },
         });
      }
   ),

   updateTokenExpiryDate: callOnAllTabs(
      "cisco.dna.core.auth/updateTokenExpiryDate",
      (expires: string) => {
         const { session } = get();
         if (!session) return;

         sharedStorage.setItem("cisco.dna.core.auth/token-expire", expires);

         set({
            refreshing: null,
            session: { ...session, expires: new Date(expires) },
         });
      }
   ),

   setTimeoutWarning: callOnAllTabs(
      "cisco.dna.core.auth/setTimeoutWarning",
      (date: string | null) => {
         const { session } = get();
         if (!session) return;

         if (date) sharedStorage.setItem("cisco.dna.core.auth/expire", date);
         else sharedStorage.removeItem("cisco.dna.core.auth/expire");

         set({
            session: {
               ...session,
               timeoutWarning: date ? new Date(date) : undefined,
            },
         });
      }
   ),

   endSession: callOnAllTabs("cisco.dna.core.auth/endSession", async () => {
      sharedStorage.removeItem("cisco.dna.core.auth/expire");
      sharedStorage.removeItem("cisco.dna.core.auth/token-expire");
      set({ session: null });
      await getLogoutUrl().then((url) => {
         // localStorage.clear();
         window.location.assign(url);
      });
   }),
}));

/**
 * Get the current store in a non reactive way
 */
export function getState(): SessionStore {
   return useStore.getState();
}

/**
 * Get the current session from the store
 */
export function getCurrentSession(): Option<Session> {
   return Some(getState().session || undefined);
}
